<template>
  <header class="q4cl-fixed-fontsize bg-white border-b border-q4cl-border shadow-card sticky top-0 z-30" role="banner">
    <div class="w-full max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
      <!-- 現在の query (?hash=... / ?debug=true 等) を保持したまま / に戻る。
           保持しないと Home が「hash/debug 無し」と判定し案内ページに飛んで
           問診票に戻れなくなる。 -->
      <router-link :to="{ path: '/', query: $route.query }" class="flex items-center gap-2 no-underline focus-visible:outline-none" :aria-label="`${orgName} ${$t('ホームへ')}`">
        <img src="/assets/icon/Q4CL_icon.png" alt="" class="w-8 h-8" aria-hidden="true" />
        <span class="text-base font-bold text-q4cl-primary tracking-tight">{{ $t(orgName) }}</span>
      </router-link>

      <nav class="flex items-center gap-0.5 sm:gap-1" :aria-label="$t('ユーティリティ')">
        <button type="button" class="btn-icon" @click="openHistory" :aria-label="$t('送信履歴を開く')">
          <i class="fa-solid fa-clock-rotate-left" aria-hidden="true"></i><span class="hidden sm:inline ml-1">{{ $t('履歴') }}</span>
        </button>
        <button v-if="translateEnabled" type="button" class="btn-icon" @click="openLanguage" :aria-label="`${$t('表示言語を切り替える')} (${$t('現在')}: ${currentLangName})`">
          <i class="fa-solid fa-globe" aria-hidden="true"></i><span class="hidden sm:inline ml-1">{{ currentLangLabel }}</span>
        </button>
        <button
          type="button"
          class="btn-icon font-bold"
          @click="cycleFontSize"
          :aria-label="`${$t('文字の大きさを変える')} (${$t('現在')}: ${$t(fontLevelLabel)})`"
          :aria-pressed="fontLevel > 0"
        >
          <span aria-hidden="true" class="text-base">A</span>
          <span class="text-[10px] align-top ml-0.5" aria-hidden="true">{{ fontLevel === 0 ? '+' : (fontLevel === 1 ? '++' : '−') }}</span>
        </button>
        <!-- 使い方ページ。 query を保持しておくことで戻る際にロゴから問診票へ復帰できる -->
        <router-link :to="{ path: '/usage', query: $route.query }" class="btn-icon no-underline" :aria-label="$t('使い方を開く')">
          <i class="fa-solid fa-circle-question" aria-hidden="true"></i><span class="hidden sm:inline ml-1">{{ $t('使い方') }}</span>
        </router-link>
      </nav>
    </div>
  </header>
</template>

<script>
/**
 * ヘッダー: ロゴ + 履歴 / 言語切替 / 文字サイズトグル / 使い方
 *
 * 文言はすべて $t("...") で翻訳経路を通す (旧 Q4CL-client/RenderQuestionnaire.vue 流)
 */
const FONT_KEY = 'fontsizeLevel';
const FONT_LEVELS = [
  { level: 0, px: '16px', label: '標準' },
  { level: 1, px: '20px', label: '大' },
  { level: 2, px: '24px', label: '特大' }
];

function applyFontLevel(level) {
  // html ルートの font-size を変えることで Tailwind の rem ベース utility が
  // 全体的にスケールする。 sticky header / footer は CSS クラス
  // `.q4cl-fixed-fontsize` で 16px に強制ロックして影響を遮断する。
  document.documentElement.style.fontSize = FONT_LEVELS[level].px;
}

function loadInitialLevel() {
  const raw = localStorage.getItem(FONT_KEY);
  if (raw !== null) return Math.max(0, Math.min(2, parseInt(raw, 10) || 0));
  // 旧版は @capacitor/storage 経由なので Web では 'CapacitorStorage.fontsizeIsLarge' が実キー。
  // Vite 版に一度だけ移行するので、 読めたら新キーへ書き戻して旧キーを削除する。
  const legacy = localStorage.getItem('fontsizeIsLarge')
    || localStorage.getItem('CapacitorStorage.fontsizeIsLarge');
  const level = legacy === 'true' ? 1 : 0;
  localStorage.removeItem('fontsizeIsLarge');
  localStorage.removeItem('CapacitorStorage.fontsizeIsLarge');
  if (level !== 0) localStorage.setItem(FONT_KEY, String(level));
  return level;
}

export default {
  name: 'AppHeader',
  setup() {
    const i18n = window.i18nStore;
    const qs = window.questionnaireStore;

    const orgName = Vue.computed(() => qs.state.userProperty?.name || 'セキュア問診票');

    // 旧旧版踏襲 (RenderQuestionnaire.vue:13,29 v-if="translate"):
    // 問診票定義に translate 設定 ('all' / 'major' / カンマ区切り) が無ければ
    // 言語ボタン自体を非表示にする。 翻訳機能をオフにしたい問診票で UI が出ない。
    const translateEnabled = Vue.computed(() => {
      const t = qs.state.qstDef?.translate;
      return !!t && t !== '';
    });

    const currentLangLabel = Vue.computed(() => {
      const lang = i18n.language;
      if (!lang) return 'JA';
      return lang.toUpperCase();
    });
    const currentLangName = Vue.computed(() => {
      const lang = i18n.language;
      if (!lang) return '日本語';
      const entry = (i18n.languages || []).find((l) => l.code === lang);
      // languages.json は english_name / local_name 形式、 フォールバックは name のみの場合あり
      return entry ? (entry.local_name || entry.english_name || entry.name || lang) : lang;
    });

    const fontLevel = Vue.ref(loadInitialLevel());
    const fontLevelLabel = Vue.computed(() => FONT_LEVELS[fontLevel.value].label);

    function cycleFontSize() {
      fontLevel.value = (fontLevel.value + 1) % FONT_LEVELS.length;
      localStorage.setItem(FONT_KEY, String(fontLevel.value));
      applyFontLevel(fontLevel.value);
    }

    Vue.onMounted(() => {
      applyFontLevel(fontLevel.value);
    });

    const openLanguage = () => window.dispatchEvent(new CustomEvent('q4cl:open-language-selector'));
    const openHistory = () => window.dispatchEvent(new CustomEvent('q4cl:open-sent-history'));

    return {
      orgName, translateEnabled, currentLangLabel, currentLangName,
      fontLevel, fontLevelLabel, cycleFontSize,
      openLanguage, openHistory
    };
  }
};
</script>
